import { Pipe, PipeTransform } from '@angular/core';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { createMarkdownExit, PluginSimple } from 'markdown-exit';
import { DYNAMIC_COMPONENT } from '../directives/dynamic-component-renderer.directive';

import mk from 'markdown-it-katex';

@Pipe({
    standalone: true,
    name: 'markdownRender'
})
export class MarkdownRenderPipe implements PipeTransform {

    private md = createMarkdownExit({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true
    });

    private condition: boolean = false;

    constructor(private sanitizer: DomSanitizer) {
        this.md.use(this.sourcesPlugin());
        this.md.use(mk);
    }

    /**
     * Transforms markdown string to HTML
     * @param value - The markdown string to render
     * @param condition - Optional condition to pass to dynamic components
     * @returns SafeHtml - Sanitized HTML content
     */
    transform(value: string | null | undefined, condition?: boolean): SafeHtml {
        if (!value) return '';

        // Ensure value is a string - log warning if not (should be handled by component)
        if (typeof value !== 'string') {
            console.error('[MarkdownRenderPipe] Received non-string value:', {
                type: typeof value,
                value: value
            });
            // Convert to string as fallback, but this should not happen
            value = String(value);
        }

        // Additional check: if value is empty or only whitespace, return empty
        if (!value.trim()) {
            return '';
        }

        this.condition = condition ?? false;

        const cleaned = this.hidePartialInlineEvidenceSources(value);
        const { markdown, replacements } = this.normalizeInlineEvidenceSources(cleaned);
        let html = this.md.render(markdown);
        html = this.replaceInlineEvidenceSourcesPlaceholders(html, replacements);
        html = this.wrapTablesWithHorizontalScroll(html);
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    /**
     * Strips unclosed inline evidence fences during streaming to prevent flickering.
     * Stage 1: Strip if evidence keyword is found in the partial content.
     * Stage 2: Strip if fence is inline (not at line start) and content is ≤10 chars
     *          (covers empty, language tag, or partial JSON before keyword arrives).
     */
    private hidePartialInlineEvidenceSources(value: string): string {
        const lastOpenFence = value.lastIndexOf('```');
        if (lastOpenFence === -1) return value;

        const afterFence = value.slice(lastOpenFence + 3);
        // Fence is closed — nothing to strip
        if (afterFence.includes('```')) return value;

        // Stage 1: keyword already arrived — strip
        const fenceContent = afterFence.toLowerCase();
        if (
            fenceContent.includes('"evidence_sources') ||
            fenceContent.includes('evidence_sources')
        ) {
            return value.slice(0, lastOpenFence);
        }

        // Stage 2: inline fence with very short content (early streaming frames)
        // Block-level fences (at line start) are skipped — they're legitimate code blocks
        const isInline = lastOpenFence > 0 && value[lastOpenFence - 1] !== '\n';
        const trimmed = afterFence.trim();
        if (isInline && trimmed.length <= 10) {
            return value.slice(0, lastOpenFence);
        }

        return value;
    }

    /**
     * Finds closed inline evidence_sources fences and replaces them with HTML
     * comment placeholders before markdown-it renders. After rendering, the
     * placeholders are swapped with the actual evidence sources component.
     * Block-level fences (at line start) are skipped — handled by sourcesPlugin.
     */
    private normalizeInlineEvidenceSources(value: string): {
        markdown: string;
        replacements: Array<{ placeholder: string; jsonContent: string; valid: boolean }>;
    } {
        const replacements: Array<{ placeholder: string; jsonContent: string; valid: boolean }> = [];
        // Match closed ```json...``` or ```...``` fences
        const fenceRegex = /```(?:json)?\s*([\s\S]*?)```/g;
        const matches: Array<{ start: number; end: number; jsonContent: string; valid: boolean }> = [];
        let match: RegExpExecArray | null;

        while ((match = fenceRegex.exec(value)) !== null) {
            const content = match[1].trim();
            // Only process fences containing evidence_sources JSON
            if (!content.includes('"evidence_sources"')) continue;

            // Validate whether the JSON is well-formed with an evidence_sources array
            let isValidSources = false;
            try {
                const parsed = JSON.parse(content);
                isValidSources = !!parsed?.evidence_sources && Array.isArray(parsed.evidence_sources);
            } catch {
                isValidSources = false;
            }

            // Skip block-level fences — sourcesPlugin handles those via markdown-it
            const isAtLineStart =
                match.index === 0 || value[match.index - 1] === '\n';
            if (isAtLineStart) {
                continue;
            }

            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                jsonContent: content,
                valid: isValidSources
            });
        }

        // Replace matches in reverse order to preserve string indices
        let markdown = value;
        for (let i = matches.length - 1; i >= 0; i--) {
            const { start, end, jsonContent, valid } = matches[i];
            const placeholder = `<!-- EVIDENCE_SOURCES_PLACEHOLDER_${i} -->`;
            replacements.unshift({ placeholder, jsonContent, valid });
            markdown =
                markdown.slice(0, start) + placeholder + markdown.slice(end);
        }

        return { markdown, replacements };
    }

    private replaceInlineEvidenceSourcesPlaceholders(
        html: string,
        replacements: Array<{ placeholder: string; jsonContent: string; valid: boolean }>
    ): string {
        let result = html;
        for (const { placeholder, jsonContent, valid } of replacements) {
            if (valid) {
                const inlineHtml = this.processSourcesElement(jsonContent, true);
                result = result.replaceAll(placeholder, inlineHtml);
            } else {
                result = result.replaceAll(placeholder, '');
            }
        }
        return result;
    }

    private wrapTablesWithHorizontalScroll(html: string): string {
        // Fast path: avoid DOM parsing when there are no tables.
        if (!html || !html.includes('<table')) {
            return html;
        }

        // DOMParser isn't available in non-browser contexts. Fail gracefully.
        if (typeof DOMParser === 'undefined') {
            return html;
        }

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const tables = Array.from(doc.querySelectorAll('table'));

            for (const table of tables) {
                const parent = table.parentElement;
                if (parent?.classList.contains('thread-table-wrapper')) {
                    continue;
                }

                const wrapper = doc.createElement('div');
                wrapper.className = 'thread-table-wrapper';

                // Insert wrapper where the table currently sits, then move the table into it.
                table.replaceWith(wrapper);
                wrapper.appendChild(table);
            }

            return doc.body.innerHTML;
        } catch {
            // If parsing fails for any reason, return original HTML instead of breaking rendering.
            return html;
        }
    }

    private sourcesPlugin(): PluginSimple {
        return (md: any) => {
            const defaultCodeBlockRender = md.renderer.rules.code_block || md.renderer.rules.fence ||
                ((tokens: any, idx: number, options: any, env: any, self: any) => self.renderToken(tokens, idx, options));

            const codeBlockHandler = (
                tokens: any,
                idx: number,
                options: any,
                env: any,
                self: any
            ) => {
                const content = tokens[idx].content.trim();

                if (content.includes('"evidence_sources"')) {
                    try {
                        const parsed = JSON.parse(content);
                        if (parsed.evidence_sources) {
                            return this.processSourcesElement(content);
                        }
                    } catch (error) {
                        return this.processSourcesLoadingElement();
                    }
                }


                if (this.condition && content.includes('"follow_ups"')) {
                    try {
                        const parsed = JSON.parse(content);
                        if (parsed.follow_ups && Array.isArray(parsed.follow_ups)) {
                            return this.processFollowUpsElement(parsed.follow_ups);
                        }
                    } catch (error) {
                        return this.processSourcesLoadingElement();
                    }
                }

                if (content.startsWith('{') && content.includes('"follow_ups"')) {
                    return this.processSourcesLoadingElement();
                }

                if (content.startsWith('{') && (content.includes('"evidence') || content.includes('"eviden'))) {
                    return this.processSourcesLoadingElement();
                }

                return defaultCodeBlockRender(tokens, idx, options, env, self);
            };

            md.renderer.rules.code_block = codeBlockHandler;
            md.renderer.rules.fence = codeBlockHandler;
        };
    }

    // private processCustomElements(html: string): string {
    //     // Match JSON blocks that appear on their own line
    //     const jsonBlockRegex = /^{[\s\S]*?}$/gm;

    //     let processedHtml = html;

    //     const matches = html.match(jsonBlockRegex);
    //     if (!matches) return processedHtml;

    //     matches.forEach(match => {
    //         try {
    //             const parsed = JSON.parse(match);

    //             if (parsed && Array.isArray(parsed.evidence_sources)) {
    //                 const replacement = this.processSourcesElement(match);
    //                 processedHtml = processedHtml.replace(match, replacement);
    //             }
    //         } catch (_) {
    //             // Ignore invalid JSON
    //         }
    //     });

    //     return processedHtml;
    // }


    private processSourcesElement(sourcesHtml: string, inline = false): string {
        const sources = this.parseSourcesJSON(sourcesHtml);
        if (sources.length === 0) return '';

        const sourcesJson = JSON.stringify(sources);
        const escapedJson = this.escapeJsonForHtml(sourcesJson);

        if (inline) {
            return `<span class="dynamic-component sources-inline" data-component="${DYNAMIC_COMPONENT.SOURCES}" data-sources-content="${escapedJson}"></span>`;
        }
        return `<div class="dynamic-component" data-component="${DYNAMIC_COMPONENT.SOURCES}" data-sources-content="${escapedJson}"></div>`;
    }

    private processFollowUpsElement(followUps: string[]): string {
        if (followUps.length === 0) return '';

        const followUpsJson = JSON.stringify(followUps);
        const escapedJson = this.escapeJsonForHtml(followUpsJson);
        const conditionAttr = this.condition !== null ? `data-condition="${this.escapeJsonForHtml(String(this.condition))}"` : '';

        return `<div class="dynamic-component" data-component="${DYNAMIC_COMPONENT.FOLLOW_UPS}" data-follow-ups-content="${escapedJson}" ${conditionAttr}></div>`;
    }

    private processSourcesLoadingElement(): string {
        return ``;
    }

    private escapeJsonForHtml(json: string): string {
        return json
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private parseSourcesJSON(sourcesContent: string): Array<{ source: string; resource: string; name: string; page_number: number; confidence: number }> {
        try {
            const cleanContent = sourcesContent.trim();

            if (!cleanContent) {
                return [];
            }

            const parsed = JSON.parse(cleanContent);

            if (parsed.evidence_sources && Array.isArray(parsed.evidence_sources)) {
                return parsed.evidence_sources;
            }

            return [];
        } catch (error) {
            console.error('Failed to parse sources JSON:', error, 'Content:', sourcesContent);
            return [];
        }
    }
}
